import { useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { DurationButton } from './DurationButton';
import classnames from 'classnames';
import { useTrackSessionCreationStep } from '@app/analytics';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: '100%',
  },
  durationsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.marginBase * 2 + 'px',
  },
  durationsContainerSmall: {
    gap: theme.marginBase,
  },
  durationIcon: {
    fill: theme.colors.grayBorder,
  },
  durationButtonSmall: {
    padding: theme.marginBase,
  },
}));

interface Props {
  name: string;
  buttonDurations: number[];
  small?: boolean;
}

export const DurationRangeSelectors = ({
  name,
  buttonDurations,
  small,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const formik = useFormikContext<any>();
  const track = useTrackSessionCreationStep();

  const setValue = useCallback(
    (value: number) => {
      if (!formik || !name) {
        return;
      }
      formik.setFieldValue(name, value);
    },
    [formik, name],
  );

  const getRangeText = useCallback(
    (value: number) => {
      const index = buttonDurations.findIndex((duration) => duration === value);
      if (index === 0) {
        return `<${buttonDurations[index]}`;
      }
      return `${buttonDurations[index - 1]}-${buttonDurations[index]}`;
    },
    [buttonDurations],
  );

  const onDurationClick = useCallback(
    (value: number) => {
      setValue(value);
      formik.setFieldValue('durationDisplayed', getRangeText(value));
      track('Duration', {
        duration: value,
        durationDisplayed: getRangeText(value),
      });
    },
    [formik, getRangeText, setValue],
  );

  return (
    <div className={classes.container}>
      <div
        className={classnames(classes.durationsContainer, {
          [classes.durationsContainerSmall]: small,
        })}
      >
        {buttonDurations.map((duration) => (
          <DurationButton
            duration={duration}
            durationText={getRangeText(duration)}
            onClick={() => onDurationClick(duration)}
            title={`add ${getRangeText(duration)}min`}
            isSelected={formik.values.duration === duration}
            small={small}
          />
        ))}
      </div>
    </div>
  );
};
